import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { globalSegmentsApi } from 'api/endpoints/global_segments.api';
import { Segment } from 'types/push.types';
import {
  GlobalSegmentCreateRequest,
  GlobalSegmentTestRequest,
  GlobalSegmentTestResponse,
} from 'types/global_segments.types';
import { RootState } from 'store';

interface GlobalSegmentsState {
  segments: Segment[];
  currentSegment: Segment | null;
  testResult: GlobalSegmentTestResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: GlobalSegmentsState = {
  segments: [],
  currentSegment: null,
  testResult: null,
  loading: false,
  error: null,
};

export const fetchGlobalSegments = createAsyncThunk(
  'globalSegments/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await globalSegmentsApi.list();
      return response.segments;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось загрузить глобальные сегменты',
      );
    }
  },
);

export const fetchGlobalSegment = createAsyncThunk(
  'globalSegments/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await globalSegmentsApi.get(id);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось загрузить сегмент',
      );
    }
  },
);

export const createGlobalSegment = createAsyncThunk(
  'globalSegments/create',
  async (data: GlobalSegmentCreateRequest, { rejectWithValue }) => {
    try {
      const response = await globalSegmentsApi.create(data);
      return response.segment;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось создать сегмент',
      );
    }
  },
);

export const updateGlobalSegment = createAsyncThunk(
  'globalSegments/update',
  async (
    {
      id,
      data,
    }: { id: number; data: Partial<GlobalSegmentCreateRequest> },
    { rejectWithValue },
  ) => {
    try {
      const response = await globalSegmentsApi.update(id, data);
      return response.segment;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось обновить сегмент',
      );
    }
  },
);

export const deleteGlobalSegment = createAsyncThunk(
  'globalSegments/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await globalSegmentsApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Не удалось удалить сегмент',
      );
    }
  },
);

export const testGlobalSegment = createAsyncThunk(
  'globalSegments/test',
  async (data: GlobalSegmentTestRequest, { rejectWithValue }) => {
    try {
      return await globalSegmentsApi.test(data);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || 'Ошибка тестирования сегмента',
      );
    }
  },
);

const globalSegmentsSlice = createSlice({
  name: 'globalSegments',
  initialState,
  reducers: {
    setCurrentGlobalSegment: (state, action: PayloadAction<Segment | null>) => {
      state.currentSegment = action.payload;
    },
    clearGlobalSegmentTestResult: state => {
      state.testResult = null;
    },
    clearGlobalSegmentsError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGlobalSegments.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalSegments.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = action.payload || [];
      })
      .addCase(fetchGlobalSegments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchGlobalSegment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSegment = action.payload;
      })
      .addCase(fetchGlobalSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createGlobalSegment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGlobalSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments.unshift(action.payload);
        state.currentSegment = action.payload;
      })
      .addCase(createGlobalSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateGlobalSegment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGlobalSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSegment = action.payload;
        const idx = state.segments.findIndex(s => s.id === action.payload.id);
        if (idx >= 0) {
          state.segments[idx] = action.payload;
        }
      })
      .addCase(updateGlobalSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteGlobalSegment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGlobalSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = state.segments.filter(s => s.id !== action.payload);
        if (state.currentSegment?.id === action.payload) {
          state.currentSegment = null;
        }
      })
      .addCase(deleteGlobalSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(testGlobalSegment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testGlobalSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.testResult = action.payload;
      })
      .addCase(testGlobalSegment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentGlobalSegment,
  clearGlobalSegmentTestResult,
  clearGlobalSegmentsError,
} = globalSegmentsSlice.actions;

export const selectGlobalSegments = (state: RootState) =>
  state.globalSegments.segments;
export const selectCurrentGlobalSegment = (state: RootState) =>
  state.globalSegments.currentSegment;
export const selectGlobalSegmentTestResult = (state: RootState) =>
  state.globalSegments.testResult;
export const selectGlobalSegmentsLoading = (state: RootState) =>
  state.globalSegments.loading;
export const selectGlobalSegmentsError = (state: RootState) =>
  state.globalSegments.error;

export default globalSegmentsSlice.reducer;